import type { CSSProperties } from "react";

export const XO_INK = "#1E1B16";
export const XO_GOLD = "#C8A44D";
export const XO_PAPER = "#F6F0E6";
export const XO_STAMP = "#B23A2F";
export const XO_FONT_STACK =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

/** 8px grid: tight 16, normal 24, section 40, hero top 72 */
export const XO_SPACE = {
  xs: 8,
  tight: 16,
  normal: 24,
  section: 40,
  heroTop: 72,
} as const;

/** Shared palette for XO Reserve / XOnvergence hero surfaces (canonical tokens). */
export const XO_CONVERGENCE_BRAND = {
  ink: XO_INK,
  gold: XO_GOLD,
  paper: XO_PAPER,
  stamp: XO_STAMP,
} as const;

export const XO_GOLD_RGB = "200, 164, 77";
export const XO_STAMP_RGB = "178, 58, 47";
export const XO_PAPER_RGB = "246, 240, 230";

/** Primary stamp radial wash — kept soft (≈12% peak) so stacked shells stay calm.
 *  Inter loads from `index.html`; stack is set on `body` / `#root` in `global.css`. */
export const xoStampWash =
  "radial-gradient(1400px 720px at 50% -8%, rgba(178,58,47,0.096), rgba(178,58,47,0.032) 42%, rgba(30,27,22,0) 62%)";

/** Secondary warm lift — subtle gold haze. */
export const xoStampWashSoft =
  "radial-gradient(900px 500px at 50% 88%, rgba(200,164,77,0.056), rgba(30,27,22,0) 55%)";

/**
 * Minimal layout shell for embeds that need the same max width as the hero stage.
 * Prefer `XOConvergenceHeroShell` for the full section (ink, washes, frame, copy).
 */
export const xoHeroColumnStyle: CSSProperties = {
  position: "relative",
  maxWidth: 1120,
  marginInline: "auto",
  padding: `${XO_SPACE.heroTop}px ${XO_SPACE.normal}px ${XO_SPACE.section}px`,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: XO_SPACE.tight,
};

/** Framed 16:9 hero stage — single soft rim + vignette (ink / gold only). */
export const xoHeroFrameStyle: CSSProperties = {
  width: "100%",
  maxWidth: 880,
  marginInline: "auto",
  aspectRatio: "16 / 9",
  borderRadius: 16,
  border: "1px solid rgba(200, 164, 77, 0.14)",
  boxShadow: "0 24px 64px -32px rgba(0, 0, 0, 0.45)",
  overflow: "hidden",
  background: "linear-gradient(180deg, rgba(0,0,0,0.22) 0%, rgba(20,17,14,0.4) 100%)",
};

/** Optional raster still: place under `public/` and reference from a component. */
export const XO_HERO_STILL_PLACEHOLDER_PATH = "/xo-reserve-hero-still.webp";

/** Public marketing surfaces: typography + text roles (Inter weights + XO Paper/Gold). */
export const xoPublicText = {
  primary: XO_PAPER,
  secondary: `rgba(${XO_PAPER_RGB}, 0.85)`,
  muted: `rgba(${XO_PAPER_RGB}, 0.55)`,
  faint: `rgba(${XO_PAPER_RGB}, 0.45)`,
  accent: XO_GOLD,
} as const;

export const xoPublicType = {
  h1: { fontWeight: 800 as const },
  h2: { fontWeight: 600 as const },
  body: { fontWeight: 400 as const },
  label: { fontWeight: 500 as const },
} as const;

/** Kicker (meta) — Inter 500, uppercase, soft gold */
export const xoPublicKickerStyle: CSSProperties = {
  fontWeight: 500,
  fontSize: "0.6875rem",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: `rgba(${XO_GOLD_RGB}, 0.63)`,
  margin: 0,
};

export const xoPublicBodyStyle: CSSProperties = {
  fontWeight: 400,
  fontSize: "1rem",
  lineHeight: 1.45,
  color: `rgba(${XO_PAPER_RGB}, 0.85)`,
};

export const xoPublicMetaStyle: CSSProperties = {
  fontWeight: 400,
  fontSize: "0.75rem",
  lineHeight: 1.45,
  color: `rgba(${XO_PAPER_RGB}, 0.55)`,
};
